import { GeoJsonLayer } from '@deck.gl/layers/typed';
import { Box, styled, useTheme } from '@mui/material';
import { BBox2d, MultiPolygon } from '@turf/helpers/dist/js/lib/geojson';
import {
  Feature,
  FeatureCollection,
  Polygon,
  bbox,
  center,
  circle,
  featureCollection,
  geometry,
  geometryCollection,
  multiPolygon,
  pointOnFeature,
  polygon,
} from '@turf/turf';
import { ThemeTypography } from 'DesignSystem';
import CustomMap, {
  ICustomMapRef,
  IMapMarker,
  IMapMarkerClickEvent,
  IMapPopup,
} from 'DesignSystem/Map/CustomMap';
import { calculateArea, radiusFromHectares } from 'DesignSystem/Map/utils/map.utils';
import { TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { IFarmSite } from 'FeatureModules/Site/types/site.types';
import useDialog from 'hooks/useDialog';
import uniqBy from 'lodash/uniqBy';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { convertHexToRGBarray } from 'utils/color.utils';
import { formatAndRoundNumber } from 'utils/number.utils';
import { v4 as uuid } from 'uuid';
import PlotInfoPopup, { IFeatureProperties } from './PlotInfoPopup';
import {
  EGeoDataIssueSeverityLevel,
  IGeoDataIssue,
} from 'FeatureModules/DataImport/types/dataset.types';

type IFarmPlotsProps = {
  plots: IPlot[];
  /**
   * In edit mode the edit button is shown in the info popup
   * @default edit
   */
  mode?: 'edit' | 'view' | 'single-plot-edit';
  /**
   * Warnings that are shown in the info popup
   */
  issues?: IGeoDataIssue[];
  /**
   * Minimum height of the map
   * @default 600px
   */
  minHeight?: number;

  /**
   * If the view should only show the neighbouring farm plots of a specific dataset
   */
  geoDataId?: string;

  onFarmPlotsUpdated?: () => void;
};

const FarmPlotsAreaSize = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: theme.custom.themeColors.grayScale[20],
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  zIndex: 3,
  right: 46,
  top: 10,
}));

const FarmPlots: FC<IFarmPlotsProps> = ({
  mode = 'edit',
  plots,
  issues,
  minHeight = 600,
  geoDataId,
  onFarmPlotsUpdated,
}) => {
  const customMapRef = useRef<ICustomMapRef>(null);
  const { openDialog, closeDialog } = useDialog();
  const [infoPopup, setInfoPopup] = useState<IMapPopup>();
  const [mapZoom, setMapZoom] = useState<number>();

  const theme = useTheme();

  const uniqueFarms = useMemo(
    () =>
      uniqBy(
        plots.map(({ farm }) => farm),
        'id'
      ),
    [plots]
  );

  const handleEditFarm = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = uniqueFarms.find(farm => farm.id === farmId);
      if (!farm) {
        throw new Error('Could not find farm');
      }

      openDialog({
        type: 'ADD_EDIT_SITE',
        props: { site: farm },
      });
    },
    [uniqueFarms, openDialog, closeDialog]
  );

  const handleEditFarmPlot = useCallback(
    (plot: IPlot) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      openDialog({
        type: 'ADD_EDIT_FARM_PLOT',
        props: {
          plot,
          geoDataId,
          onPlotsUpdated: onFarmPlotsUpdated,
        },
      });
    },
    [geoDataId, onFarmPlotsUpdated, openDialog, closeDialog]
  );

  const getPlotHexColor = useCallback(
    (properties?: Pick<IFeatureProperties, 'issues'>, opacity = 255) => {
      if (
        properties?.issues?.some(issue => issue.severityLevel === EGeoDataIssueSeverityLevel.ERROR)
      ) {
        // If issues contain severity level ERROR show plot in red
        return convertHexToRGBarray(theme.custom.themeColors.error[100], opacity);
      } else if (
        properties?.issues?.some(
          issue => issue.severityLevel === EGeoDataIssueSeverityLevel.WARNING
        )
      ) {
        // If issues contain severity level WARNING show plot in yellow
        return convertHexToRGBarray(theme.custom.themeColors.accent[100], opacity);
      }

      // Else show plot in baby blue default color
      return convertHexToRGBarray(theme.custom.colors.babyBlue, opacity);
    },
    [theme.custom]
  );

  const handleFeatureClick = useCallback(
    (featureProperties: IFeatureProperties) => {
      setInfoPopup(prev => {
        if (prev && prev.id === featureProperties.plot.id) {
          return undefined;
        }
        return {
          id: featureProperties.plot.id,
          coordinate: featureProperties.center,
          content: (
            <PlotInfoPopup
              viewOnly={mode === 'view'}
              infoItem={featureProperties}
              onEditPlotClick={handleEditFarmPlot}
              onEditFarmClick={handleEditFarm}
            />
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infoPopup, setInfoPopup]
  );

  const handlePopupClose = useCallback(() => setInfoPopup(() => undefined), [setInfoPopup]);

  /**
   * This feature collections contains all the cultivation areas of the farm as single polygons with their properties
   * To be able to show the info popup when clicking on the cultivation area on a single polygon we need to have the center of each polygon
   * To be able to show centered markers of the one cultivation area we need to have the center of the multi polygon
   */
  const features: FeatureCollection<Polygon | MultiPolygon, IFeatureProperties> = useMemo(() => {
    const features: Feature<Polygon | MultiPolygon, IFeatureProperties>[] = plots.map(plot => {
      const issuesOfPlot = issues?.filter(
        issue =>
          (issue.entityType === 'PLOT' && issue.entityId === plot.id) ||
          (issue.entityType === 'SITE' && issue.siteId === plot.farm.id)
      );
      const feature = plot.feature;
      const featureGeometry = geometry(feature.geometry.type, feature.geometry.coordinates);
      const properties: IFeatureProperties = {
        plot,
        issues: issuesOfPlot,
        // Center of the polygon always inside the polygon
        center:
          feature.geometry.type === 'Point'
            ? feature.geometry.coordinates
            : (pointOnFeature(featureGeometry).geometry.coordinates as [number, number]),
      };
      if (feature.geometry.type === 'Polygon') {
        return polygon(feature.geometry.coordinates, properties, { id: uuid() });
      } else if (feature.geometry.type === 'MultiPolygon') {
        return multiPolygon(feature.geometry.coordinates, properties, { id: uuid() });
      } else if (feature.geometry.type === 'Point') {
        return circle(
          feature.geometry.coordinates,
          // 112.8 meters is the radius of the circle of 4ha
          plot.sizeHa ? radiusFromHectares(plot.sizeHa) : 112.8,
          {
            units: 'meters',
            properties,
          }
        );
      }
      throw new Error(`Plot with ID ${plot.id} has an invalid geometry type`);
    });
    return featureCollection(features);
  }, [plots, issues]);

  const featureLayer = useMemo(
    () =>
      new GeoJsonLayer<IFeatureProperties>({
        id: 'farm-plots',
        data: features,
        getLineWidth: 2,
        pickable: true,
        getFillColor: feature => getPlotHexColor(feature?.properties as IFeatureProperties, 50),
        getLineColor: feature => getPlotHexColor(feature?.properties as IFeatureProperties),
        onClick: info => handleFeatureClick(info.object.properties),
      }),
    [features, handleFeatureClick, getPlotHexColor]
  );

  const pointFeatures: IMapMarker[] = useMemo(
    () =>
      plots
        .filter(({ feature }) => feature.geometry.type === 'Point')
        .map(({ id: plotId, feature, farmId }) => {
          const issuesOfPlot = issues?.filter(
            issue =>
              (issue.entityType === 'PLOT' && issue.entityId === plotId) ||
              (issue.entityType === 'SITE' && issue.siteId === farmId)
          );
          const customIcon: TIconName = 'map-marker';
          return {
            id: plotId,
            coordinate: feature.geometry.coordinates as [number, number], // can only be a point here since we filtered for it,
            options: {
              customIcon,
              color: `rgba(${getPlotHexColor({ issues: issuesOfPlot })})`,
            },
          };
        }),
    [plots, issues, getPlotHexColor]
  );

  // Event id is the id of the clicked plot
  const handleMarkerClick = useCallback(
    (event: IMapMarkerClickEvent) => {
      if (!event.id) {
        console.error('No id found for the clicked marker');
        return;
      }
      // If the click was on a polygon marker, we need to find the polygon in the feature collection
      const polygonFeature = features.features.find(
        polygon => polygon.properties.plot.id === event.id
      );
      if (polygonFeature) {
        customMapRef.current?.customFitBounds(bbox(polygonFeature) as BBox2d);
        handleFeatureClick(polygonFeature.properties);
      } else {
        const plot = plots.find(plot => plot.id === event.id);
        if (!plot) {
          console.error('Could not find plot for the clicked marker with id ' + event.id);
          return;
        }
        const issuesOfPlot = issues?.filter(
          issue =>
            (issue.entityType === 'PLOT' && issue.entityId === event.id) ||
            (issue.entityType === 'SITE' && issue.siteId === plot.farmId)
        );
        handleFeatureClick({
          plot,
          issues: issuesOfPlot,
          center: event.coordinate as [number, number],
        });
      }
    },
    [features, customMapRef, plots, issues, handleFeatureClick]
  );

  /**
   * Markers are only shown when the zoom level is higher than 10 to identify the cultivation areas if the world is zoomed out
   */
  const markers: IMapMarker[] = useMemo(
    () =>
      features.features.reduce((prev, feature) => {
        const coordinate = feature.properties.center;
        if (!coordinate) {
          return prev;
        }
        // If marker for that cultivation area was already added, don't add it again
        if (prev.findIndex(prevMarker => prevMarker.coordinate === coordinate) !== -1) {
          return prev;
        }
        return [
          ...prev,
          {
            id: feature.properties.plot.id,
            coordinate,
            options: {
              customIcon: 'map-marker',
              color: `rgba(${getPlotHexColor(feature.properties)})`,
              focusOnClick: false,
            },
          },
        ];
      }, [] as IMapMarker[]),

    [features, getPlotHexColor]
  );

  const totalAreaSizeHa = useMemo(() => calculateArea(features), [features]);

  /**
   * Center of all plots, this will be used for the initial view state from which point the map will zoom in to focus all available plots
   * Initial zoom level is 2 so the user can also identify the continent + country of the visible plots
   */
  const initialCenter = useMemo(() => {
    const allFeatures = [
      ...features.features.map(f => f.geometry),
      ...pointFeatures.map(f => geometry('Point', f.coordinate)),
    ];
    if (!allFeatures.length) {
      return undefined;
    }
    return center(geometryCollection(allFeatures));
  }, [features, pointFeatures]);

  const numberOfPlots = useMemo(() => features.features.length, [features]);

  useEffect(() => {
    customMapRef.current?.centerFeatures();
    setInfoPopup(undefined); // Close the info popup when data changed
  }, [customMapRef, pointFeatures, pointFeatures]);

  return (
    <Box width="100%" height="100%" position="relative" borderRadius={6}>
      <FarmPlotsAreaSize>
        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
          Total area
        </ThemeTypography>
        <ThemeTypography variant="BODY_SMALL">
          {formatAndRoundNumber(totalAreaSizeHa, 2)} ha
        </ThemeTypography>
        {numberOfPlots >= 1 && mode !== 'single-plot-edit' && (
          <Box mt={1}>
            <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
              Nr of plots
            </ThemeTypography>
            <ThemeTypography variant="BODY_SMALL">{numberOfPlots}</ThemeTypography>
          </Box>
        )}
      </FarmPlotsAreaSize>
      <CustomMap
        initialViewState={
          initialCenter
            ? {
                latitude: initialCenter.geometry.coordinates[1],
                longitude: initialCenter.geometry.coordinates[0],
              }
            : undefined
        }
        customRef={customMapRef}
        markers={mapZoom && mapZoom < 12 ? [...markers, ...pointFeatures] : []}
        layers={[featureLayer]}
        infoPopup={infoPopup}
        mapStyle="satellite"
        style={{
          minHeight,
          height: '100%',
          width: '100%',
          borderRadius: 6,
        }}
        config={{
          enableMapStyleToggle: true,
          enableCenterButton: true,
          centerButtonText: mode === 'single-plot-edit' ? 'Center plot' : 'Center plot(s)',
        }}
        onZoom={event => setMapZoom(event.viewState.zoom)}
        onMapLoad={map => setMapZoom(map.getZoom())}
        onMarkerPopupClose={handlePopupClose}
        onMarkerClick={handleMarkerClick}
      />
    </Box>
  );
};

export default FarmPlots;
